import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { VDateSelectorComponent } from "@app/directives/_veri/v-date-selector/v-date-selector.component";
import { IUser } from "@app/interfaces/IUser";
import { EngineerJobStatus, IEngineerJob, IEngineerJobBookingWithEngineerMappings } from "@app/main/modules/foundation/foundation.interfaces";
import { EngineerJobService } from "@app/main/modules/foundation/_services/engineer-job.service";
import { IAccount } from "@app/services/account.service";
import { Observable, Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { IEngineerJobBookingMapping } from "../../../_models/IEngineerJobBookingMapping";
import { IUserGroup } from "../../../_models/IUserGroup";
import { EngineerJobBookingMappingService } from "../../../_services/engineer-job-booking-mapping.service";
import { EngineerJobBookingService } from "../../../_services/engineer-job-booking.service";
import { UserGroupService } from "../../../_services/user-group.service";
import { CalendarDirection } from "@app/enum/CalendarDirection.enum";
import './job-editor.component.scss';

@Component({
    selector: 'job-editor',
    template: require('./job-editor.component.html')
})
export class JobEditorComponent implements OnInit, OnChanges, OnDestroy {
    @Input() job: IEngineerJob;
    @Input() jobBooking: IEngineerJobBookingWithEngineerMappings;
    @Input() accounts: IAccount[];
    @Input() editMode: boolean = false;

    @Output() jobSaved = new EventEmitter<IEngineerJob>();
    @Output() jobBookingSaved = new EventEmitter<IEngineerJobBookingWithEngineerMappings>();

    @ViewChild(VDateSelectorComponent) dateSelector: VDateSelectorComponent;

    //--

    amendedJob: IEngineerJob;
    amendedJobBooking: IEngineerJobBookingWithEngineerMappings;
    jobEditorForm: FormGroup;
    userGroups$: Observable<IUserGroup[]>;
    selectedUserGroupId: number;
    selectedUserGroup: IUserGroup;

    engineerJobStatus = EngineerJobStatus;

    saving: boolean;
    jobIsClosed: boolean = false;
    showParentCompany: boolean;

    formValid: boolean;

    CalendarDirection = CalendarDirection;

    private destroy$ = new Subject<void>();
    constructor(
        private readonly engineerJobBookingService: EngineerJobBookingService,
        private readonly engineerJobBookingMappingService: EngineerJobBookingMappingService,
        private readonly engineerJobService: EngineerJobService,
        private readonly userGroupService: UserGroupService
    ) { }

    ngOnInit(): void {
        this.load();
    }

    ngOnChanges(): void {
        this.load();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.unsubscribe();
    }

    reset(): void {
        this.load();
    }

    saveJob(): void {
        this.saving = true;
        this.saveOrUpdateJob();
    }

    accountSearchPredicate(account: IAccount, text: string): boolean {
        return account.CompanyName.toLowerCase().includes(text.toLowerCase());
    }

    accountClicked(account: IAccount): void {
        this.amendedJob.AccountId = account.Id;
    }

    onAutocompleteCleared(): void {
        this.amendedJob.AccountId = null;
    }

    onDateSelectionChanged(dates: Date[]): void {
        if (dates.length < 2) {
            return;
        }
        this.editMode && this.jobEditorForm.markAsDirty();
        this.amendedJobBooking.StartUtc = dates[0];
        this.amendedJobBooking.EndUtc = dates[1];
    }

    onSelectedEngineersChanged(engineers: IUser[]): void {
        this.jobEditorForm.markAsDirty();
        this.clearSelectedParentCompany();
        this.amendedJobBooking.assignedEngineers = [...engineers];
    }

    onSelectedParentCompanyChanged(userGroup: IUserGroup): void {
        this.jobEditorForm.markAsDirty();
        this.amendedJobBooking.assignedGroupId = userGroup.Id;
        this.amendedJobBooking.assignedGroup = userGroup;
        this.selectedUserGroupId = userGroup.Id;
        this.selectedUserGroup = userGroup;
    }

    toggleShowParentCompany(toggle: boolean): void {
        this.jobEditorForm.markAsDirty();
        this.amendedJobBooking.assignedEngineers = [];
        this.amendedJobBooking.mappings = [];
        this.showParentCompany = toggle;
        !this.showParentCompany && this.clearSelectedParentCompany();
    }

    clearSelectedParentCompany(): void {
        this.amendedJobBooking.assignedGroupId = null;
        this.amendedJobBooking.assignedGroup = null;
        this.selectedUserGroup = null;
        this.selectedUserGroupId = null;
    }


    groupSearchPredicate(group: IUserGroup, text: string): boolean {
        return group.Description.toLowerCase().includes(text.toLowerCase());
    }


    // Getters

    get canSave(): boolean {
        if (!this.job ||
            this.jobIsClosed ||
            !this.amendedJob.AccountId ||
            !this.jobEditorForm?.valid ||
            (!this.jobEditorForm?.dirty && this.editMode) ||
            this.dateSelector.calendar.invalidSelection
        ) {
            return false;
        }

        return true;
    }

    //--

    private load(): void {
        if (!this.editMode) {
            this.createNewJob();
            this.setupJobEditorForm();
            return;
        }

        this.userGroups$ = this.userGroupService.getUserGroups();

        this.setAmendedJob();
        this.setupJobEditorForm();
        this.jobIsClosed = this.engineerJobService.jobIsClosed(this.job);
        this.setSelectedUserGroup();
    }

    private createNewJob(): void {
        this.amendedJob = { ...this.job };
        this.amendedJobBooking = { ...this.job.jobBooking };
        this.amendedJobBooking.assignedEngineers = [];
        this.amendedJobBooking.mappings = [];
    }

    private setAmendedJob(): void {
        this.amendedJob = { ...this.job };
        this.amendedJobBooking = { ...this.jobBooking };
        this.amendedJobBooking.assignedEngineers = this.jobBooking?.assignedEngineers ? [...this.jobBooking.assignedEngineers] : [];
    }

    private setupJobEditorForm(): void {
        this.jobEditorForm = new FormGroup({
            'Description': new FormControl(this.amendedJob.Description, Validators.maxLength(150)),
            'EngineerInstruction': new FormControl(this.amendedJob.EngineerInstruction)
        });

        this.jobEditorForm.statusChanges.pipe(takeUntil(this.destroy$)).subscribe((status: string) => {
            if (status === "INVALID") {
                return;
            }
            this.amendedJob = {
                ...this.amendedJob,
                Description: this.jobEditorForm.value.Description,
                EngineerInstruction: this.jobEditorForm.value.EngineerInstruction
            }
        });
    }

    private setSelectedUserGroup(): void {
        if (!this.jobBooking) {
            return;
        }

        this.selectedUserGroupId = this.jobBooking.assignedGroupId;
        if (this.selectedUserGroupId) {
            this.toggleShowParentCompany(true);
        }
    }

    private saveOrUpdateJob(): void {
        if (this.editMode && this.amendedJob.Status !== EngineerJobStatus.Cancelled) {
            this.deleteCancelledAssignments();
            this.saveNewEngineerMappings();
        }

        this.engineerJobService.createUpdateEngineerJob(this.amendedJob).pipe(take(1)).subscribe(job => {
            this.amendedJob.Id = job.Id;
            this.amendedJobBooking.JobId = job.Id;
            this.amendedJobBooking.assignedGroup = this.selectedUserGroup;
            this.amendedJobBooking.assignedGroupId = this.selectedUserGroupId;
            this.saveJobBooking();
            this.jobSaved.emit(this.amendedJob);

        })
    }

    private saveJobBooking(): void {
        const selectedEngineersCopy = [...this.amendedJobBooking.assignedEngineers];

        this.engineerJobBookingService.createUpdateEngineeringJobBooking(this.amendedJobBooking, this.amendedJob.AccountId).pipe(take(1)).subscribe(jobBooking => {
            this.amendedJobBooking = jobBooking;
            this.amendedJobBooking.assignedEngineers = [...selectedEngineersCopy];
            this.amendedJobBooking.assignedGroupId = this.selectedUserGroupId;
            this.amendedJobBooking.assignedGroup = this.selectedUserGroup;
            this.saving = false;
            this.jobBookingSaved.emit(this.amendedJobBooking);
        });
    }

    private deleteCancelledAssignments(): void {
        if (!this.jobBooking?.mappings?.length) {
            return;
        }

        this.jobBooking.mappings.forEach(mapping => {
            if (this.engineersMatchMapping(mapping) || this.selectedGroupMatchMapping(mapping)) // selected group Id matches mapping
            {
                return;
            }

            this.engineerJobBookingMappingService.deleteJobBookingMapping(mapping).pipe(take(1)).subscribe();
        })
    }

    private engineersMatchMapping(mapping :IEngineerJobBookingMapping) : boolean {
        return !mapping.UserGroupId && !!this.amendedJobBooking.assignedEngineers.find(eng => eng.Id === mapping.UserId);
    }

    private selectedGroupMatchMapping(mapping: IEngineerJobBookingMapping): boolean {
        return mapping.UserGroupId && this.amendedJobBooking.assignedGroupId == mapping.UserGroupId;
    }

    private saveNewEngineerMappings(): void {
        if (this.amendedJobBooking.assignedGroupId) {
            let mapping: IEngineerJobBookingMapping = {
                UserGroupId: this.amendedJobBooking.assignedGroupId,
                Id: 0,
                EngineerJobBookingId: this.amendedJobBooking.Id
            }
            this.amendedJobBooking.mappings.push(mapping);
            this.engineerJobBookingMappingService.addUpdateEngineerJobBookingMapping(mapping).pipe(take(1)).subscribe(() => {
                this.jobBookingSaved.emit(this.amendedJobBooking);
            });
            return;
        }

        this.amendedJobBooking.assignedEngineers.forEach(engineer => {
            if (this.mappingExists(engineer.Id)) {
                return;
            }
            this.engineerJobBookingMappingService.createJobBookingMappingsFromEngineerId(engineer.Id, this.amendedJobBooking.Id).pipe(take(1)).subscribe((mapping) => {
                this.amendedJobBooking.mappings.push(mapping);
            });
        })
        this.engineerJobBookingService.setSelectedJobBooking(this.amendedJobBooking);
    }

    private mappingExists(userId: number): boolean {
        if (!this.jobBooking?.assignedEngineers?.length) {
            return false;
        };

        return !!this.jobBooking.assignedEngineers.find(eng => eng.Id === userId);
    }


}
